import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import {
    FRI_SCHEDULE,
    HourForm,
    MON_SCHEDULE,
    SAT_SCHEDULE,
    SUN_SCHEDULE,
    ScheduleForm,
    THU_SCHEDULE,
    TUE_SCHEDULE,
    WED_SCHEDULE,
} from '../common/HourForm'
import { WorkflowPage } from '../workflows/WorkflowPage'

function dayIsEnabled(day: number, schedule: number | undefined) {
    if (!schedule) return false
    return Boolean(schedule & day)
}

export function SetupOnboardingBusinessHoursPage() {
    const me = PSApplication.me()
    const { logAndDisplayError } = useErrorHandler()
    const { advanceOnboardingState } = useOnboardingContext()
    const navigate = useNavigate()
    const [isSaving, setIsSaving] = useState(false)

    usePageTitle('Business Hours')

    const [scheduleForm, setScheduleForm] = useState<ScheduleForm>({
        monStart: me?.monStartHour ?? 9,
        monEnd: me?.monEndHour ?? 18,
        tueStart: me?.tueStartHour ?? 9,
        tueEnd: me?.tueEndHour ?? 18,
        wedStart: me?.wedStartHour ?? 9,
        wedEnd: me?.wedEndHour ?? 18,
        thuStart: me?.thuStartHour ?? 9,
        thuEnd: me?.thuEndHour ?? 18,
        friStart: me?.friStartHour ?? 9,
        friEnd: me?.friEndHour ?? 18,
        satStart: me?.satStartHour ?? undefined,
        satEnd: me?.satEndHour ?? undefined,
        sunStart: me?.sunStartHour ?? undefined,
        sunEnd: me?.sunEndHour ?? undefined,
        schedule: me?.schedule ?? 0x1f,
    })

    const isError = useMemo(() => {
        return Boolean(
            (dayIsEnabled(MON_SCHEDULE, scheduleForm.schedule) &&
                scheduleForm.monStart !== undefined &&
                scheduleForm.monEnd !== undefined &&
                scheduleForm.monStart >= scheduleForm.monEnd) ||
                (dayIsEnabled(TUE_SCHEDULE, scheduleForm.schedule) &&
                    scheduleForm.tueStart !== undefined &&
                    scheduleForm.tueEnd !== undefined &&
                    scheduleForm.tueStart >= scheduleForm.tueEnd) ||
                (dayIsEnabled(WED_SCHEDULE, scheduleForm.schedule) &&
                    scheduleForm.wedStart !== undefined &&
                    scheduleForm.wedEnd !== undefined &&
                    scheduleForm.wedStart >= scheduleForm.wedEnd) ||
                (dayIsEnabled(THU_SCHEDULE, scheduleForm.schedule) &&
                    scheduleForm.thuStart !== undefined &&
                    scheduleForm.thuEnd !== undefined &&
                    scheduleForm.thuStart >= scheduleForm.thuEnd) ||
                (dayIsEnabled(FRI_SCHEDULE, scheduleForm.schedule) &&
                    scheduleForm.friStart !== undefined &&
                    scheduleForm.friEnd !== undefined &&
                    scheduleForm.friStart >= scheduleForm.friEnd) ||
                (dayIsEnabled(SAT_SCHEDULE, scheduleForm.schedule) &&
                    scheduleForm.satStart !== undefined &&
                    scheduleForm.satEnd !== undefined &&
                    scheduleForm.satStart >= scheduleForm.satEnd) ||
                (dayIsEnabled(SUN_SCHEDULE, scheduleForm.schedule) &&
                    scheduleForm.sunStart !== undefined &&
                    scheduleForm.sunEnd !== undefined &&
                    scheduleForm.sunStart >= scheduleForm.sunEnd)
        )
    }, [scheduleForm])

    const hasChanges = useMemo(() => {
        return (
            scheduleForm.schedule !== me?.schedule ||
            scheduleForm.monStart !== me?.monStartHour ||
            scheduleForm.monEnd !== me?.monEndHour ||
            scheduleForm.tueStart !== me?.tueStartHour ||
            scheduleForm.tueEnd !== me?.tueEndHour ||
            scheduleForm.wedStart !== me?.wedStartHour ||
            scheduleForm.wedEnd !== me?.wedEndHour ||
            scheduleForm.thuStart !== me?.thuStartHour ||
            scheduleForm.thuEnd !== me?.thuEndHour ||
            scheduleForm.friStart !== me?.friStartHour ||
            scheduleForm.friEnd !== me?.friEndHour ||
            scheduleForm.satStart !== me?.satStartHour ||
            scheduleForm.satEnd !== me?.satEndHour ||
            scheduleForm.sunStart !== me?.sunStartHour ||
            scheduleForm.sunEnd !== me?.sunEndHour
        )
    }, [scheduleForm, me])

    const user = me.clone()

    const onNextHandler = useCallback(async () => {
        setIsSaving(true)
        user.monStartHour = dayIsEnabled(MON_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.monStart
            : undefined
        user.monEndHour = dayIsEnabled(MON_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.monEnd
            : undefined
        user.tueStartHour = dayIsEnabled(TUE_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.tueStart
            : undefined
        user.tueEndHour = dayIsEnabled(TUE_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.tueEnd
            : undefined
        user.wedStartHour = dayIsEnabled(WED_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.wedStart
            : undefined
        user.wedEndHour = dayIsEnabled(WED_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.wedEnd
            : undefined
        user.thuStartHour = dayIsEnabled(THU_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.thuStart
            : undefined
        user.thuEndHour = dayIsEnabled(THU_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.thuEnd
            : undefined
        user.friStartHour = dayIsEnabled(FRI_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.friStart
            : undefined
        user.friEndHour = dayIsEnabled(FRI_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.friEnd
            : undefined
        user.satStartHour = dayIsEnabled(SAT_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.satStart
            : undefined
        user.satEndHour = dayIsEnabled(SAT_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.satEnd
            : undefined
        user.sunStartHour = dayIsEnabled(SUN_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.sunStart
            : undefined
        user.sunEndHour = dayIsEnabled(SUN_SCHEDULE, scheduleForm.schedule)
            ? scheduleForm.sunEnd
            : undefined

        user.schedule = scheduleForm.schedule

        const result = await RecordFactory.getInstance().sendUser(user)

        setIsSaving(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        await advanceOnboardingState()
    }, [advanceOnboardingState, user, scheduleForm])

    return (
        <>
            <WorkflowPage
                onClose={() => navigate('/')}
                onBack={() => navigate('/')}
                backButtonTitle="Home"
                footerContent={
                    <div className="flex justify-end">
                        <Button
                            intent="primary"
                            type="submit"
                            disabled={isError || isSaving}
                            isLoading={isSaving}
                            onClick={onNextHandler}
                        >
                            Next
                        </Button>
                    </div>
                }
            >
                <Heading size="xl" className="mt-[5px]">
                    Your hours
                </Heading>
                <Paragraph size="md" className="mt-4 text-center text-gray-600">
                    Add your business hours and let clients know when you're available.
                </Paragraph>
                <WorkflowPage.Card className="mt-12">
                    <HourForm scheduleForm={scheduleForm} onUpdateScheduleForm={setScheduleForm} />
                </WorkflowPage.Card>
            </WorkflowPage>

            <NavigationBlockerPrompt when={hasChanges} allowNextPaths={[/\/onboarding\/.*/]} />
        </>
    )
}
